import { useCallback, useEffect } from 'react'
import { trackEvent } from '../events/trackEvent'
import useAccessControl from '../../legacy/components/AccessControl/useAccessControl'
import { FeatureToggle } from '../../access-control/featureToggles'
import { type NewCookieEvent } from './cookieChange.types'
import { useNewCookieDetection } from './cookieChange.hooks'

/**
 * Component that monitors for new cookies being set and tracks unexpected ones.
 * This component renders nothing but sets up cookie detection monitoring.
 */
export const GlobalCookieDetection = (): null => {
  const safeHandler = useCallback((event: NewCookieEvent) => {
    if (event.isUnexpected) {
      trackEvent({
        id: 'security.unexpected_cookie',
        description: 'Unexpected cookie detected',
        data: {
          cookieName: event.name,
          cookieDomain: event.domain,
          timestamp: event.timestamp.toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        },
      })
    }
  }, [])

  const initializeCookieDetection = useNewCookieDetection(safeHandler)
  const hasAccess = useAccessControl()

  useEffect(() => {
    if (
      !hasAccess({
        featureToggles: FeatureToggle.IppCookieDetectionEnabled,
      })
    ) {
      return
    }

    let cleanup: (() => void) | undefined

    const setupDetection = async () => {
      cleanup = await initializeCookieDetection()
    }

    setupDetection()

    return () => {
      if (cleanup) {
        cleanup()
      }
    }
  }, [initializeCookieDetection, hasAccess])

  // This component doesn't render anything
  return null
}
